import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { NotificationProvider } from '@/shared/components/notification/notification';
import { AuthProvider } from '@/contexts/AuthContext';
import ProtectedRoute from '@/components/ProtectedRoute';
import Dashboard from './pages/Dashboard';
import ScanScreen from './pages/ScanScreen';
import InboundFlow from './pages/InboundFlow';
import SlotResult from './pages/SlotResult';
import OutboundFlow from './pages/OutboundFlow';
import Login from './pages/Login';
import AllSlotList from './pages/AllSlotList';
import OverduePackages from './pages/OverduePackages';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import PickedPackagesList from './pages/PickedPackagesList';
import NotFound from './pages/NotFound';
import Layout from './components/warehouse/Layout';
import { HeaderProvider } from './contexts/HeaderContext';
import * as Sentry from '@sentry/react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import i18n from './i18n';

const queryClient = new QueryClient();

// Wrap the routes component for Sentry routing instrumentation
const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

interface ErrorFallbackProps {
  error: any;
  resetError: () => void;
}

// Read translations off the i18n instance rather than the useTranslation hook:
// this renders after a crash, and if i18n itself is what broke, the hook could
// throw again inside the boundary. Falls back to English.
const tSafe = (key: string, fallback: string): string => {
  try {
    const value = i18n.t(key);
    return value && value !== key ? value : fallback;
  } catch {
    return fallback;
  }
};

const ErrorFallback = ({ error, resetError }: ErrorFallbackProps) => (
  <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4 text-foreground selection:bg-primary selection:text-primary-foreground">
    <div className="w-full max-w-md border border-border bg-card/60 backdrop-blur-md p-6 rounded-lg shadow-xl text-center">
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10 text-destructive animate-pulse">
        <AlertTriangle className="h-8 w-8" />
      </div>
      <h2 className="mb-2 text-2xl font-bold tracking-tight">
        {tSafe('something_went_wrong', 'Something went wrong')}
      </h2>
      <p className="mb-6 text-sm text-muted-foreground">
        {tSafe(
          'unexpected_error_occurred',
          'An unexpected application error occurred. Sentry has been notified of this issue.',
        )}
      </p>

      {/* The message is the only part users need. The stack is collapsed behind
          a toggle for developers, and `break-words` stops long bundler URLs in
          it from overflowing the card. */}
      <div className="mb-6 rounded-md border border-border/50 bg-muted p-3 text-left">
        <p className="font-mono text-xs font-semibold text-destructive break-words">
          {error?.name || 'Error'}: {error?.message || String(error)}
        </p>
        {error?.stack && (
          <details className="mt-2">
            <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
              {tSafe('technical_details', 'Technical details')}
            </summary>
            <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-words font-mono text-[11px] leading-relaxed text-muted-foreground">
              {error.stack}
            </pre>
          </details>
        )}
      </div>

      {/* Stacked on phones, side-by-side from 420px up. "Erneut versuchen" only
          stops clipping at exactly 380px (measured), so 420px leaves headroom for
          longer translations and OS font scaling. The Button base style is
          whitespace-nowrap, so a too-narrow button clips rather than wraps.
          basis-0 + min-w-0 keeps the pair equal width when side-by-side. */}
      <div className="flex flex-col gap-3 min-[420px]:flex-row min-[420px]:gap-4">
        <Button
          variant="outline"
          className="w-full gap-2 min-[420px]:flex-1 min-[420px]:basis-0 min-[420px]:min-w-0"
          onClick={() => {
            window.location.href = '/';
          }}
        >
          <Home className="h-4 w-4 shrink-0" />
          {tSafe('go_home', 'Home')}
        </Button>
        <Button
          className="w-full gap-2 min-[420px]:flex-1 min-[420px]:basis-0 min-[420px]:min-w-0"
          onClick={resetError}
        >
          <RefreshCw className="h-4 w-4 shrink-0" />
          {tSafe('try_again', 'Try Again')}
        </Button>
      </div>
    </div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <NotificationProvider>
        <AuthProvider>
          <Sentry.ErrorBoundary
            fallback={({ error, resetError }) => <ErrorFallback error={error} resetError={resetError} />}
          >
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <HeaderProvider>
                <SentryRoutes>
                  <Route path="/login" element={<Login />} />
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Dashboard />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/scan"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <ScanScreen />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/inbound"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <InboundFlow />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/slot-result"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <SlotResult />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/outbound"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <OutboundFlow />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/slot-list"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <AllSlotList />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/overdue-packages"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <OverduePackages />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/analytics"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <Layout>
                          <AnalyticsDashboard />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/picked-packages"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <PickedPackagesList standalone={true} />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route path="*" element={<NotFound />} />
                </SentryRoutes>
              </HeaderProvider>
            </BrowserRouter>
          </Sentry.ErrorBoundary>
        </AuthProvider>
      </NotificationProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
